/**
 * Route Tree — TanStack Router programmatic routing
 *
 * Constitution: design/02-platform-baseline/web-runtime/FRONTEND_ARCHITECTURE.md
 * - _authenticated layout: auth-protected routes with AdminShell
 * - Auth pages (sign-in, forgot-password) are public, outside _authenticated
 * - Route structure mirrors canon:
 *     routes/
 *     ├── _authenticated/  → dashboard, noi-dung, cong-dong, kiem-duyet, nguoi-dung, he-thong
 *     └── auth/            → sign-in, forgot-password
 *
 * Plans 7.2 — decomposed into family route modules under routes/<family>/routes.tsx.
 * Each family module exports a children array composed on authenticatedRoute;
 * this file keeps only the root/layout/auth/dashboard routes and the tree assembly.
 */
import { lazy } from "react";
import { createRootRoute, createRoute, Outlet, redirect } from "@tanstack/react-router";
import { Toaster } from "sonner";

import { CommandMenu } from "@/components/command-menu";
import { NavigationProgress } from "@/components/navigation-progress";
import { SkipToMain } from "@/components/skip-to-main";

import { AdminShell } from "@/components/layout/admin-shell";
import { getCurrentUser } from "@/lib/auth";

import { withSuspense } from "./route-helpers.js";

// ── Family route modules (Plans 7.2) ────────────────────────────────

import { noiDungRoutes } from "@/routes/noi-dung/routes.js";
import { niemKinhRoutes } from "@/routes/noi-dung/niem-kinh/index.js";
import { congDongRoutes } from "@/routes/cong-dong/routes.js";
import { kiemDuyetRoutes } from "@/routes/kiem-duyet/routes.js";
import { nguoiDungRoutes } from "@/routes/nguoi-dung/routes.js";
import { heThongRoutes } from "@/routes/he-thong/routes.js";
import { phapLuatRoutes } from "@/routes/phap-luat/routes.js";
import { suKienRoutes } from "@/routes/su-kien/routes.js";
import { phongSinhRoutes } from "@/routes/phong-sinh/routes.js";
import { donPhapBaoRoutes } from "@/routes/don-phap-bao/routes.js";
import { soRoutes } from "@/routes/so/routes.js";
import { banThoRoutes } from "@/routes/ban-tho/routes.js";
import { hoTroRoutes } from "@/routes/ho-tro/routes.js";

// ── Lazy page imports (root keeps only auth + dashboard pages) ──────

const SignInPage = lazy(() => import("@/features/auth/sign-in").then((mod) => ({ default: mod.SignInPage })));
const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/forgot-password").then((mod) => ({ default: mod.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("@/features/auth/reset-password").then((mod) => ({ default: mod.ResetPasswordPage })),
);
const DashboardOverview = lazy(() =>
  import("@/features/dashboard").then((mod) => ({ default: mod.DashboardOverview })),
);

// ── Root Route ───────────────────────────────────────────────────────

export const rootRoute = createRootRoute({
  component: () => (
    <>
      <SkipToMain />
      <NavigationProgress />
      <Outlet />
      <CommandMenu />
      <Toaster richColors position="top-center" />
    </>
  ),
});

// ── _authenticated Layout Route ──────────────────────────────────────
// Canon: routes/_authenticated/ — auth guard + AdminShell wrapper
// All protected routes are children of this layout route.

export const authenticatedRoute = createRoute({
  id: "_authenticated",
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    const user = await getCurrentUser();
    if (!user) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirect pattern
      throw redirect({ to: "/auth/dang-nhap" });
    }
  },
  component: () => (
    <AdminShell>
      <Outlet />
    </AdminShell>
  ),
});

// ── Auth Routes (public — outside _authenticated) ────────────────────

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/dang-nhap",
  component: withSuspense(SignInPage),
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/quen-mat-khau",
  component: withSuspense(ForgotPasswordPage),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/dat-lai-mat-khau",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: withSuspense(ResetPasswordPage),
});

// ── Protected Routes (children of _authenticated) ────────────────────

// Dashboard
const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/",
  component: withSuspense(DashboardOverview),
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/dashboard",
  component: withSuspense(DashboardOverview),
});

// ── Route Tree Assembly ──────────────────────────────────────────────
// Family children arrays are added in the same order their routes appear
// in the canonical route tree (Plans 7.2 decomposition).

export const routeTree = rootRoute.addChildren([
  // Public auth routes
  signInRoute,
  forgotPasswordRoute,
  resetPasswordRoute,

  // Protected routes — all under _authenticated layout
  authenticatedRoute.addChildren([
    indexRoute,
    dashboardRoute,
    // Nội dung — static (tao-moi) before dynamic ($publicId)
    ...noiDungRoutes,
    niemKinhRoutes,
    // Cộng đồng
    ...congDongRoutes,
    // Kiểm duyệt
    ...kiemDuyetRoutes,
    // Người dùng
    ...nguoiDungRoutes,
    // Hệ thống
    ...heThongRoutes,
    // Hỗ trợ
    ...hoTroRoutes,
    // Thanh Tịnh Pháp — Tuân thủ Pháp luật
    ...phapLuatRoutes,
    // Thanh Tịnh Pháp — Sự kiện Phật pháp (static before dynamic)
    ...suKienRoutes,
    // Thanh Tịnh Pháp — Phóng sinh
    ...phongSinhRoutes,
    // Thanh Tịnh Pháp — Đơn Pháp Bảo
    ...donPhapBaoRoutes,
    // Thanh Tịnh Pháp — Sớ (Ngôi Nhà Nhỏ)
    ...soRoutes,
    // Thanh Tịnh Pháp — Bàn thờ
    ...banThoRoutes,
  ]),
]);
